import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { saveMaterial, unsaveMaterial } from "../../services/materialsService";
import { MaterialCardMedia } from "./MaterialPreview";

// Presentational teacher card reused by the dashboard "Recommended Materials"
// section and the teacher Materials library. Reuses MaterialPreview +
// ResourceTypeBadge so the per-type visuals match the admin manage page.
//
// Props:
//   material        — MaterialRead incl my_state
//   matchedQuestions — (recommended only) the question texts that triggered it;
//                      rendered as a "recommended because you answered" note
export default function MaterialTeacherCard({ material, matchedQuestions = [] }) {
  const { t } = useTranslation();
  // The media + title now navigate to the in-app detail page, which owns
  // mark-read; the card no longer opens the external URL or marks read on click.
  const detailHref = `/my-materials/${material.uuid}`;

  // Optimistic local copy of the save state — the card owns this so a save
  // reflects instantly without re-fetching the whole list.
  const [saved, setSaved] = useState(!!material.my_state?.saved);
  const [saving, setSaving] = useState(false);

  // The read badge is read straight from props — mark-read now happens on the
  // detail page, so the flag updates whenever the parent swaps in a fresh
  // material object (e.g. a list re-fetch) rather than on a card click.
  const read = !!material.my_state?.read;

  // Keep the local save flag aligned when the parent swaps in a fresh material.
  useEffect(() => {
    setSaved(!!material.my_state?.saved);
  }, [material.uuid, material.my_state?.saved]);

  const toggleSave = async () => {
    if (saving) return;
    const next = !saved;
    setSaved(next); // optimistic
    setSaving(true);
    try {
      if (next) {
        await saveMaterial({ uuid: material.uuid });
      } else {
        await unsaveMaterial({ uuid: material.uuid });
      }
    } catch {
      setSaved(!next); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const readBadge = read ? (
    <span className="absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50/95 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
      </svg>
      {t("materials.read")}
    </span>
  ) : null;

  return (
    <div className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition overflow-hidden">
      <MaterialCardMedia
        material={material}
        headerExtra={readBadge}
        linkTo={detailHref}
      >
        {matchedQuestions.length > 0 && (
          <div className="mt-1 rounded-lg bg-indigo-50/70 border border-indigo-100 px-2.5 py-2">
            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-wide mb-0.5">
              {t("materials.recommendedBecause")}
            </p>
            <ul className="space-y-0.5">
              {matchedQuestions.map((q, i) => (
                <li
                  key={i}
                  className="text-[11px] text-indigo-900/70 leading-snug line-clamp-2"
                >
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(material.subjects || []).length > 0 && (
          <div className="mt-auto pt-1 flex flex-wrap gap-1">
            {material.subjects.map((s) => (
              <span
                key={s.uuid}
                className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-bold"
              >
                {s.name}
              </span>
            ))}
          </div>
        )}
      </MaterialCardMedia>

      <div className="flex items-center justify-between gap-1 px-3 py-2 border-t border-slate-100 bg-slate-50/60">
        <button
          type="button"
          onClick={toggleSave}
          disabled={saving}
          aria-pressed={saved}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
            saved
              ? "text-indigo-600 hover:bg-white"
              : "text-slate-600 hover:text-indigo-600 hover:bg-white"
          }`}
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill={saved ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z" />
          </svg>
          {saved ? t("materials.saved") : t("materials.save")}
        </button>
        <Link
          to={detailHref}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition"
        >
          {t("materials.open")}
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
